import type { GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { TableGeneratorResult } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { pluralize, singularize, toCamelCase, toKebabCase, toPascalCase } from '@cli-utils/text.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { resolveEntityFilters } from '@next-adapter/generators/entity-filters/resolve-entity-filters.js'
import { renderTableRenderer } from '@next-adapter/templates/renderers/generators/table.js'

export function generateTable(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): TableGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const camelPlural = toCamelCase(plural)
  const tableFileName = `${toKebabCase(plural)}-table.tsx`
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const resolvedFilters = resolveEntityFilters(schema).map((filter) => ({
    ...filter,
    variableName: toCamelCase(filter.key),
    fromVariableName: `${toCamelCase(filter.key)}From`,
    toVariableName: `${toCamelCase(filter.key)}To`
  }))
  const filterProps = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [`${filter.fromVariableName}?: string`, `${filter.toVariableName}?: string`]
        : [`${filter.variableName}?: string`]
    )
    .join('\n  ')
  const filterParams = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [filter.fromVariableName, filter.toVariableName]
        : [filter.variableName]
    )
    .join(', ')
  const hasEdit = schema.actions?.edit ?? false

  const content = renderTableRenderer({
    kind: 'renderTableClientModule',
    props: {
      plural: Plural,
      schemaName: schema.name,
      singular: Singular,
      actionImportPath,
      filterProps,
      filterParams,
      camelPlural,
      schemaLabel: schema.label,
      hasEdit,
      adminRoutePath
    }
  })

  return {
    files: [createGeneratedFile(`${pagesDir}/${schema.name}/${tableFileName}`, content)]
  }
}
